import angular from "angular";

import {
  BootstrapStyle,
  SessionUserCredential,
  Translations,
  WorkflowData,
  WorkflowEvent,
} from "@salaxy/core";

import {
  CredentialService,
  SessionService,
  UiHelpers,
  WorkflowService,
} from "../../services";

import { ApiCrudObjectController } from "../bases/ApiCrudObjectController";

import { WorkflowEditorConfig } from "./WorkflowEditorConfig";

/**
 * Controller for implementing workflow logic.
 */
export class WorkflowController implements angular.IController {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = [
    "WorkflowService",
    "UiHelpers",
    "SessionService",
    "CredentialService",
  ];

  /**
   * API CRUD controller for the object.
   */
  public apiCtrl: ApiCrudObjectController<any, any>;

  public showWorkflowAssign: boolean;

  /**
   * Single wfType to show.
   */
  public wfType: string;

  /** Returns true if the update is in progress */
  public isUpdating = false;

  /**
   * Function that is called when a new workflow action has happened..
   * Function has the following locals:  item: the parent item for workflow, isDelete: true/false if the workflow action was a removal of the event.
   */
  public onChange: (params: {
    /** Item details after change */
    item: any,
    /** Delete/creation information */
    isDelete: boolean,
  }) => void;

  /** Read only flag */
  public readOnly: boolean;

  private _events: WorkflowEvent[] = [];

  /**
   * Creates a new WorkflowController
   * @ignore
   */
  constructor(
    protected workflowService: WorkflowService,
    protected uiHelpers: UiHelpers,
    protected sessionService: SessionService,
    protected credentialService: CredentialService
  ) {
  }

  /**
   * Implement IController
   */
  public $onInit() {
    this.showWorkflowAssign = this.showWorkflowAssign !== false;
    // no initializations
  }

  /** Returns the events of given type. */
  public get events(): WorkflowEvent[] {
    const eventsOfType = ((this.apiCtrl.current as any)?.workflowData?.events ?? []).filter((x) => x.type == this.wfType);
    this.modifyArray(eventsOfType, this._events);
    return this._events;
  }

  /**
   * Returns the current workflow event of the given type.
   * @param wfType Event type of the workflow event.
   * @returns Returns the current workflow event of the given type.
   */
  public getWorkflowEvent(wfType: string): WorkflowEvent {
    return this.getSingleWorkflowEvent(wfType);
  }

  /**
   * Returns true if the workflow of the current item has an event of the given type.
   * @param wfType Event type of the workflow event.
   * @returns Returns true if the current item contains an event of the given type.
   */
  public hasWorkflowEvent(wfType: string): boolean {
    return !!this.getWorkflowEvent(wfType);
  }

  /**
   * Adds/updates the workflow event for the current item.
   * @param wfEvent Workflow event to add/update.
   * @returns Reloaded item.
   */
  public saveWorkflowEvent(wfEvent: WorkflowEvent): Promise<any> {
    if (this.isUpdating) {
      return;
    }
    this.isUpdating = true;
    return this.workflowService.saveWorkflowEvent(this.apiCtrl, wfEvent).then((result) => {
      this.isUpdating = false;
      if (this.onChange) {
      this.onChange({ item: result, isDelete: false });
      }
      return result;
    });
  }

  /**
   * Deletes the given event or all events with given type.
   * @param wfIdOrType Id or type of the workflow event.
   * @returns - Reloaded item.
   */
  public deleteWorkflowEvent(wfIdOrType: string): Promise<any> {
    if (this.isUpdating) {
      return;
    }
    this.isUpdating = true;
    return this.workflowService.deleteWorkflowEvent(this.apiCtrl, wfIdOrType).then((result) => {
      this.isUpdating = false;
      if (this.onChange) {
        this.onChange({ item: result, isDelete: true });
      }
      return result;
    });
  }

  /**
   * Shows a workflow editor for the given event.
   * Saves the event and reloads the item.
   * @param config Configuration for the worklow editor.
   * @param wfEvent Workflow event for the editor.
   * @returns - Reloaded item.
   */
  public showWorkflowEventEditor(config: WorkflowEditorConfig, wfEvent: WorkflowEvent): Promise<any> {
    if (!this.apiCtrl.current) {
      return Promise.resolve(this.apiCtrl.current);
    }

    config.title = config.title || "SALAXY.NG1.WorkFlowIssueComponent.showWorkflowEventEditorTitle";

    return this.uiHelpers.openEditDialog(
      "salaxy-components/modals/workflow/WorkflowEventEdit.html",
      wfEvent,
      {
        ...config,
        readOnly: this.readOnly,
      }
    ).then((dialogResult) => {
      if (dialogResult.action === "ok") {
        return this.saveWorkflowEvent(dialogResult.item);
      } else {
        return Promise.resolve(this.apiCtrl.current);
      }
    });
  }

  /** Returns avatar for the given credential. */
  public getCredential(credentialId: string): SessionUserCredential {
    return (this.credentials.find((x) => x.id === credentialId) || {});
  }

  /** Gets the credetial of the current assigned-to person or undefined if there is no assignment. */
  public getAssignedTo(): SessionUserCredential | undefined {
    const assignedTo = this.getWorkflowEvent("PartnerMessageAssignedTo")?.assignedTo;
    if (!assignedTo) {
      return undefined;
    }
    return this.getCredential(assignedTo);
  }

  /** Returns credentials for the current account. */
  public get credentials(): SessionUserCredential[] {
    return this.workflowService.credentials;
  }

  /** Returns the credential for current session */
  public get self(): SessionUserCredential {
    return this.workflowService.self;
  }

  /**
   * Responds to button click with appropriate UI logic.
   * @param type The button that was clicked.
   * @param credential The credential to assign to. Only for type "assign". Use undefined to remove assignment.
   */
  public buttonClick(type: "close" | "assign" | "issue", credential?: SessionUserCredential): void {
    switch (type) {
      case "close":
        if (this.hasWorkflowEvent("PartnerMessageClosed")) {
          this.uiHelpers.showConfirm("SALAXY.NG1.WorkFlowIssueComponent.deleteConfirmations.close").then((result: boolean) => {
            if (result) {
              this.deleteWorkflowEvent("PartnerMessageClosed");
            }
          });
        } else {
          this.saveWorkflowEvent({
            type: "PartnerMessageClosed",
            ui: BootstrapStyle.Success,
            message: Translations.get("SALAXY.UI_Terms.checked")
          });
        }
        return;
      case "issue":
        if (this.hasWorkflowEvent("PartnerMessageIssue")) {
          this.uiHelpers.showConfirm("SALAXY.NG1.WorkFlowIssueComponent.deleteConfirmations.issue").then((result: boolean) => {
            if (result) {
              this.deleteWorkflowEvent("PartnerMessageIssue");
            }
          });
        } else {
          this.showWorkflowEventEditor(
            { title: undefined, showUi: false },
            { type: "PartnerMessageIssue", ui: BootstrapStyle.Danger, message: "" }
          );
        }
        return;
      case "assign":
        if (credential) {
          this.saveWorkflowEvent({
            type: "PartnerMessageAssignedTo",
            ui: BootstrapStyle.Primary,
            message: credential.email || credential.avatar.displayName,
            assignedTo: credential.id,
          });
        } else {
          this.deleteWorkflowEvent("PartnerMessageAssignedTo");
        }
        return;
      default:
        throw new Error("Not implemented: " + type);
    }
  }

  private modifyArray(
    source: any[],
    target: any[]) {
    if (angular.equals(source, target)) {
      return;
    }
    target.splice(0, target.length);
    target.push(...source);
  }

  private getSingleWorkflowEvent(wfType: string): WorkflowEvent {
    if (!this.apiCtrl.current || !wfType) {
      return null;
    }

    const workflowData: WorkflowData = (this.apiCtrl.current as any)?.workflowData;
    if (!workflowData || !workflowData.events) {
      return null;
    }
    return workflowData.events.find((x) => (x.type || "").toLowerCase() === wfType.toLowerCase());
  }
}
